import type {
  AmazonProductScanFormat,
  BinScanFormat,
  DivisionMeta,
  DivisionScanFormat,
  GenericScanFormat,
  InboundReturnScanFormat,
  MerchantCreditScanFormat,
  ProductScanFormat,
  PurchaseOrderScanFormat,
  PurchaseOrderSessionScanFormat,
  ScanResult,
  ScanType,
  ServiceTicketScanFormat,
  TrackingScanFormat,
  UserLoginScanFormat,
} from './types.js'

import { MERCHANT_CREDIT_TYPE_NAME_MAP } from '../merchantCredit/constants.js'

export const formatProductBarcode = (
  barcode: string,
  type: ProductScanFormat['subtype'] = 'GTIN-16',
): string => {
  let formattedBarcode = '0000000000000000' + barcode
  let typeLength = 16
  switch (type) {
    case 'GTIN-12':
      typeLength = 12
      break
    case 'GTIN-13':
      typeLength = 13
      break
    case 'GTIN-16':
      typeLength = 16
      break
    case 'Alphanumeric':
      // * An alphanumeric code has no fixed-width numeric form — the trimmed raw value IS its
      // * normalized value (zero-padding would corrupt a variable-length FNSKU / custom code).
      // * Consumers reconstruct the 16-char DB form via formatProductBarcode(code, 'GTIN-16').
      return barcode.trim()
  }
  formattedBarcode = formattedBarcode.substring(
    formattedBarcode.length - typeLength,
    formattedBarcode.length,
  )
  return formattedBarcode
}

// checksum calculation for GTIN-8, GTIN-12, GTIN-13, GTIN-14, and SSCC
// based on http://www.gs1.org/barcodes/support/check_digit_calculator
// https://gist.githubusercontent.com/spig/897768/raw/d294f7902584d83a040f7f7dcf5371bc345f397f/validate_barcode.js
// https://gist.github.com/spig/897768
export const isValidGTIN = (barcode = ''): boolean => {
  if (
    barcode.length < 8 ||
    barcode.length > 18 ||
    (barcode.length !== 8 &&
      barcode.length !== 12 &&
      barcode.length !== 13 &&
      barcode.length !== 14 &&
      barcode.length !== 18)
  ) {
    return false
  }

  const lastDigit = Number(barcode.substring(barcode.length - 1))
  let checkSum = 0
  if (isNaN(lastDigit)) {
    return false
  }

  const arr = barcode
    .substring(0, barcode.length - 1)
    .split('')
    .reverse()
  let oddTotal = 0
  let evenTotal = 0

  for (let i = 0; i < arr.length; i++) {
    const value = arr?.[i]
    if (value !== undefined && isNaN(+value)) {
      return false
    }
    if (i % 2 === 0) {
      oddTotal += Number(value) * 3
    } else {
      evenTotal += Number(value)
    }
  }
  checkSum = (10 - ((evenTotal + oddTotal) % 10)) % 10
  return checkSum === lastDigit
}
export const portableBinPrefixes: Record<string, BinScanFormat['subtype']> = {
  B: 'Bin',
  N: 'Receiving',
  R: 'Return',
  T: 'Transfer',
  F: 'FBA',
  Q: 'Quarantine',
  C: 'ChannelOrder',
  S: 'SalesPerson',
  U: 'Unknown',
}
export const internalBarcodeScanTest = (barcode = ''): boolean => {
  return barcode[0] === '/' && barcode[barcode.length - 1] === '/'
}
// USPS Tracking  9400100000000000000000
const isUSPSTracking = RegExp(/^94001[0-9]{17}\b/)
// Priority Mail  9205500000000000000000
const isUSPSPriorityMail = RegExp(/^92055[0-9]{17}\b/)
// Certified Mail 9407300000000000000000
const isUSPSCertifiedMail = RegExp(/^94073[0-9]{17}\b/)
// Collect On Delivery Hold For Pickup 9303300000000000000000
const isUSPSCollectOnDeliveryHoldForPickup = RegExp(/^93033[0-9]{17}\b/)
// Global Express Guaranteed  8200000000
const isUSPSGlobalExpressGuaranteed = RegExp(/^82[0-9]{8}\b/)
// Priority Mail Express International  EC000000000US
const isUSPSPriorityMailExpressInternational = RegExp(/^EX[0-9]{9}US\b/)
// Priority Mail Express  9270100000000000000000, EA000000000US
const isUSPSPriorityMailExpress = RegExp(/^92701[0-9]{17}\b/)
const isUSPSPriorityMailExpress2 = RegExp(/^EA[0-9]{9}US\b/)
// Priority Mail International  CP000000000US
const isUSPSPriorityMailInternational = RegExp(/^CP[0-9]{9}US\b/)
// Registered Mail  9208800000000000000000
const isUSPSRegisteredMail = RegExp(/^92088[0-9]{17}\b/)
// Signature Confirmation 9202100000000000000000
const isUSPSSignatureConfirmation = RegExp(/^92021[0-9]{17}\b/)
// Ground 9622001560001234567100794808390594, 9622001560001234567100794808390594, 9632001560123456789900794808390594
const isFedExGround = RegExp(/^96[22|32][0-9]{30}\b/)
const isUPS = RegExp(
  /\b(1Z ?[0-9A-Z]{3} ?[0-9A-Z]{3} ?[0-9A-Z]{2} ?[0-9A-Z]{4} ?[0-9A-Z]{3} ?[0-9A-Z]|[\dT]\d\d\d ?\d\d\d\d ?\d\d\d)\b/,
)
const isFedEx1 = /(\b96\d{20}\b)|(\b\d{15}\b)|(\b\d{12}\b)/
const isFedEx2 = RegExp(/\b((98\d\d\d\d\d?\d\d\d\d|98\d\d) ?\d\d\d\d ?\d\d\d\d( ?\d\d\d)?)\b/)
const isFedEx3 = RegExp(/^[0-9]{15}\b/)
const isUSPS1 = RegExp(/(\b\d{30}\b)|(\b91\d+\b)|(\b\d{20}\b)/)
const isUSPS2 = RegExp(/^E\D{1}\d{9}\D{2}$|^9\d{15,21}$/)
const isUSPS3 = RegExp(/^91[0-9]+$/)
const isUSPS4 = RegExp(/^[A-Za-z]{2}[0-9]+US$/)
export const isFedExTrackingNumber = (barcode = ''): boolean => {
  return (
    (barcode.match(isFedEx1)?.length ?? 0) > 0 ||
    (barcode.match(isFedEx2)?.length ?? 0) > 0 ||
    (barcode.match(isFedEx3)?.length ?? 0) > 0 ||
    (barcode.match(isFedExGround)?.length ?? 0) > 0
  )
}
export const isUPSTrackingNumber = (barcode = ''): boolean => {
  return (barcode.match(isUPS)?.length ?? 0) > 0
}
export const isUSPSTrackingNumber = (barcode = ''): boolean => {
  return (
    (barcode.match(isUSPSTracking)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSPriorityMail)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSCertifiedMail)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSCollectOnDeliveryHoldForPickup)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSGlobalExpressGuaranteed)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSPriorityMailExpressInternational)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSPriorityMailExpress)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSPriorityMailExpress2)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSPriorityMailInternational)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSRegisteredMail)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSSignatureConfirmation)?.length ?? 0) > 0 ||
    (barcode.match(isUSPS1)?.length ?? 0) > 0 ||
    (barcode.match(isUSPS2)?.length ?? 0) > 0 ||
    (barcode.match(isUSPS3)?.length ?? 0) > 0 ||
    (barcode.match(isUSPS4)?.length ?? 0) > 0 ||
    (barcode.match(isUSPSTracking)?.length ?? 0) > 0
  )
}
export const trackingNumberScanTest = (barcode = ''): boolean => {
  return (
    isUSPSTrackingNumber(barcode) || isUPSTrackingNumber(barcode) || isFedExTrackingNumber(barcode)
  )
}
export const productScanTest = (barcode = ''): boolean => {
  return (barcode.match(/\d{8}(?:\d{4,6,8})?/)?.length ?? 0) > 0
}
export const amazonProductScanTest = (barcode = ''): boolean => {
  return (barcode.match(/^([BX][\dA-Z]{9}|\d{9}(X|\d))$/)?.length ?? 0) > 0
}
const isProductUPC = /^[0-9]{10,13}$/
// alphanumeric (>=1 letter) product barcode; see isProductBarcode below
const productBarcodePattern = /^(?=.*[A-Za-z])[A-Za-z0-9._-]{4,48}$/
/**
 * Product barcode that is not a valid GTIN: non-GTIN numeric (10-13 digits, e.g. a 10-digit custom
 * code) or an alphanumeric code (Amazon FNSKU X0053X37CX, custom SS/BS prefixed codes). Deliberately
 * broad — evaluated LAST in `parseScan`, so it only claims scans that no more specific format
 * (tracking, gift card, wrapped division, GTIN) already matched. Mirrors the legacy
 * `barcodeParsing.isProductBarcode` (blackfisk @ ad028b970).
 */
export const isProductBarcode = (barcode = ''): boolean => {
  const data = String(barcode)
  return isProductUPC.test(data) || productBarcodePattern.test(data)
}
export const productBarcodeScanTest = (barcode = ''): boolean => isProductBarcode(barcode)
/**
 * Scan↔UPC comparison shared by every device-scan line matcher (PO receiving, transfer receive,
 * inbound return, order fulfillment, outbound pick). Exact case-insensitive match on the raw value
 * handles alphanumeric codes; the numeric-only zero-padded comparison preserves the legacy
 * tolerance (EAN-13 vs UPC-A leading zeros, scans of a numeric suffix) WITHOUT the digit-strip
 * false matches (e.g. "SKI-A" and "SKI-B" both stripped to "" and matched each other). Mirrors the
 * legacy `barcodeParsing.matchProductUPC`.
 */
export const matchProductUPC = (scanValue?: string | null, productUPC?: string | null): boolean => {
  const scan = String(scanValue ?? '')
    .trim()
    .toUpperCase()
  const upc = String(productUPC ?? '')
    .trim()
    .toUpperCase()
  if (!scan || !upc) {
    return false
  }
  if (scan === upc) {
    return true
  }
  if (/^\d+$/.test(scan) && /^\d+$/.test(upc)) {
    return upc.padStart(16, '0').includes(scan.padStart(16, '0'))
  }
  return false
}
/** A product's identifier fields a scan can resolve against. */
export interface ProductScanTarget {
  upc?: string | null
  normalizedUPC?: string | null
}
/**
 * Match a scanned code against a product's identifier fields (used by the scan-driven flows: E1
 * auto-add, E2 transfer receive, FBA order packing). `matchProductUPC` covers case-insensitive raw
 * equality and the numeric zero-pad tolerance against `upc` / `normalizedUPC`; a `normalizedUPC`
 * that carries the 16-char zero-prefixed form — an FNSKU-patterned `upc` stored as
 * `000000X0053X37CX` — resolves via `formatProductBarcode(scan, 'GTIN-16') === normalizedUPC`.
 *
 * Both identifier fields are consulted: `normalizedUPC` is the padded `upc`, but the column is
 * nullable until the app syncs it and the raw/case arms of `matchProductUPC` diverge between the two
 * forms — so each is tested, then the padded bridge covers a stored normalized form.
 */
export const matchProductScan = (
  scan: string | null | undefined,
  target?: ProductScanTarget | null,
): boolean => {
  if (!target) {
    return false
  }
  if (matchProductUPC(scan, target.upc) || matchProductUPC(scan, target.normalizedUPC)) {
    return true
  }
  const normalized = (target.normalizedUPC ?? '').trim()
  const scanValue = (scan ?? '').trim()
  if (!normalized || !scanValue) {
    return false
  }
  return formatProductBarcode(scanValue, 'GTIN-16') === normalized
}
const UserBadgeRegex = /^\/QL(?<userId>\d+)\/(?<checksum>[A-Z0-9a-z]+)\/$/
export const userBadgeScanTest = (barcode = ''): boolean => {
  return UserBadgeRegex.test(barcode)
}

const MerchantCreditGiftRegex = /^GC(?<referenceId>[A-Z0-9]{14})$/
export const merchantCreditGiftScanTest = (barcode = ''): boolean => {
  return MerchantCreditGiftRegex.test(barcode)
}

const MerchantCreditEGiftRegex = /^EGC(?<referenceId>[A-Z0-9]{13})$/
export const merchantCreditEgiftScanTest = (barcode = ''): boolean => {
  return MerchantCreditEGiftRegex.test(barcode)
}
export const merchantCreditScanTest = (barcode = ''): boolean => {
  return merchantCreditGiftScanTest(barcode) || merchantCreditEgiftScanTest(barcode)
}

const PurchaseOrderSessionGroupRegex = /^\/SUM-(?<hash>[a-zA-Z0-9]+)\/$/
export const purchaseOrderSessonGroupScanTest = (barcode = ''): boolean => {
  return PurchaseOrderSessionGroupRegex.test(barcode)
}

const PurchaseOrderRegex = /^\/POV-(?<purchaseOrderId>\d+)-(?<purchaseOrderSessionId>\d+)\/$/
export const purchaseOrderSessionScanTest = (barcode = ''): boolean => {
  return PurchaseOrderRegex.test(barcode)
}
export const serviceTicketScanTest = (barcode = ''): boolean => {
  return (barcode.match(/^\/SVC-[0-9]+\/$/)?.length ?? 0) > 0
}
export const inboundReturnScanTest = (barcode = ''): boolean => {
  return (barcode.match(/^\/IBR-[0-9]+\/$/)?.length ?? 0) > 0
}
export const chaosBarcodeScanTest = (barcode = ''): boolean => {
  return (
    (barcode.match(divisionRegEx)?.length ?? 0) > 0 ||
    (barcode.match(portableBinRegEx)?.length ?? 0) > 0
  )
}
const divisionRegEx = RegExp(
  /([A-Z0-9]{1,5})-(([0-9]{2})([A-Z]{2})([0-9]{2})([A-Z]{2})-([A-Z0-9]{2,6})|([0-9]{2})([A-Z]{2})([0-9]{2})([A-Z]{2})|([0-9]{2})([A-Z]{2})([0-9]{2})|([0-9]{2})([A-Z]{2})|([0-9]{2})|([A-Z]{1}[A-Z0-9]{1,5}))/,
)
const portableBinRegEx = RegExp(/([A-Z0-9]{1,5})-([A-Z]{1}[A-Z0-9]{5})/)
export const parseChaosBarcodeScan = (
  barcode = '',
): BinScanFormat | DivisionScanFormat | GenericScanFormat => {
  const divisionSegments = divisionRegEx.exec(barcode)
  const portableBinSegments = portableBinRegEx.exec(barcode)
  // * Check if it's a PGU
  if (portableBinSegments) {
    const bin: DivisionMeta = {
      warehousePrefix: portableBinSegments?.[1] ?? '',
      warehouseZone: null,
      warehouseZoneRow: null,
      warehouseZoneRowSection: null,
      warehouseZoneRowSectionShelf: null,
      warehouseBin: portableBinSegments?.[2] ?? null,
      barcode: `${portableBinSegments[1]}-${portableBinSegments[2]}`,
    }
    const binInitialCharacter = bin?.warehouseBin?.slice(0, 1) ?? 'U'
    const ParsedScan: BinScanFormat = {
      type: 'WAREHOUSE_BIN',
      subtype: portableBinPrefixes?.[binInitialCharacter] ?? 'Unknown',
      meta: bin,
    }
    return ParsedScan
  } else if (divisionSegments) {
    const filteredDivisionSegments = divisionSegments.filter((segment) => segment)
    const division: DivisionMeta = {
      warehousePrefix: filteredDivisionSegments[1] ?? '',
      warehouseZone: filteredDivisionSegments[3] ?? null,
      warehouseZoneRow: filteredDivisionSegments[4] ?? null,
      warehouseZoneRowSection: filteredDivisionSegments[5] ?? null,
      warehouseZoneRowSectionShelf: filteredDivisionSegments[6] ?? null,
      warehouseBin: filteredDivisionSegments[7] ?? null,
      barcode: `${filteredDivisionSegments[0]}`,
    }
    let divisionType: DivisionScanFormat['subtype'] = 'Zone'
    if (division.warehouseBin) {
      divisionType = 'Bin'
    } else if (division.warehouseZoneRowSectionShelf) {
      divisionType = 'Shelf'
    } else if (division.warehouseZoneRowSection) {
      divisionType = 'Section'
    } else if (division.warehouseZoneRow) {
      divisionType = 'Row'
    } else {
      divisionType = 'Zone'
    }
    const ParsedScan: DivisionScanFormat = {
      type: 'WAREHOUSE_DIVISION',
      subtype: divisionType,
      meta: division,
    }
    return ParsedScan
  }
  const ParsedScan: GenericScanFormat = {
    type: 'UNKNOWN_FORMAT',
    subtype: 'Merchant',
  }
  return ParsedScan
}
export const parseTrackingNumber = (barcode = ''): TrackingScanFormat => {
  const isUSPS = isUSPSTrackingNumber(barcode)
  const isUPS = isUPSTrackingNumber(barcode)
  const isFedEx = isFedExTrackingNumber(barcode)
  let subtype: TrackingScanFormat['subtype'] = 'USPS'
  if (isUSPS) {
    subtype = 'USPS'
  } else if (isUPS) {
    subtype = 'UPS'
  } else if (isFedEx) {
    subtype = 'FedEx'
  }
  return {
    type: 'TRACKING_NUMBER',
    subtype,
  }
}
export const parseAmazonProductBarcode = (barcode = ''): AmazonProductScanFormat => {
  return {
    type: 'FBA_SKU',
    subtype: 'Amazon',
    meta: {
      normalized: barcode.trim(),
    },
  }
}
export const parseProductBarcode = (barcode = ''): ProductScanFormat => {
  if (/[A-Za-z]/.test(barcode)) {
    return {
      type: 'PRODUCT_GTIN',
      subtype: 'Alphanumeric',
      meta: {
        normalized: formatProductBarcode(barcode, 'Alphanumeric'),
        isValidCheckDigit: false,
      },
    }
  }
  let subtype: ProductScanFormat['subtype'] = 'GTIN-12'
  if (barcode.length === 16) {
    subtype = 'GTIN-16'
  } else if (barcode.length === 14) {
    subtype = 'GTIN-14'
  } else if (barcode.length === 13) {
    subtype = 'GTIN-13'
  } else if (barcode.length === 8) {
    subtype = 'GTIN-8'
  }
  return {
    type: 'PRODUCT_GTIN',
    subtype,
    meta: {
      normalized: formatProductBarcode(barcode, subtype),
      isValidCheckDigit: isValidGTIN(barcode),
    },
  }
}
export const parseMerchantCredit = (barcode = ''): MerchantCreditScanFormat => {
  const eGiftMatch = MerchantCreditEGiftRegex.exec(barcode)
  if (eGiftMatch?.groups?.referenceId) {
    return {
      type: 'MERCHANT_CREDIT',
      subtype: 'eGift',
      meta: {
        referenceId: eGiftMatch.groups.referenceId,
        merchantCreditTypeId: MERCHANT_CREDIT_TYPE_NAME_MAP?.['Electronic Gift Card']?.id ?? 0,
      },
    }
  }

  const giftMatch = MerchantCreditGiftRegex.exec(barcode)
  if (giftMatch?.groups?.referenceId) {
    return {
      type: 'MERCHANT_CREDIT',
      subtype: 'Gift',
      meta: {
        referenceId: giftMatch.groups.referenceId,
        merchantCreditTypeId: MERCHANT_CREDIT_TYPE_NAME_MAP?.['Gift Card']?.id ?? 0,
      },
    }
  }

  throw new Error('Barcode is neither a Gift nor eGift')
}
export const parseInboundReturn = (barcode = ''): InboundReturnScanFormat => {
  const inboundReturnId = +(barcode.replace(/\//g, '').split('-')?.[1] ?? 0)
  return {
    type: 'INBOUND_RMA',
    subtype: inboundReturnId ? 'Merchant' : 'Amazon',
    meta: {
      inboundReturnId,
    },
  }
}
export const parsePurchaseOrder = (barcode = ''): PurchaseOrderScanFormat => {
  const match = PurchaseOrderRegex.exec(barcode)
  return {
    type: 'PURCHASE_ORDER',
    subtype: 'Merchant',
    meta: {
      prefix: 'POV',
      purchaseOrderId: +(match?.groups?.purchaseOrderId ?? 0),
      purchaseOrderSessionId: +(match?.groups?.purchaseOrderSessionId ?? 0),
    },
  }
}
export const parsePurchaseOrderSession = (barcode = ''): PurchaseOrderSessionScanFormat => {
  const match = PurchaseOrderSessionGroupRegex.exec(barcode)
  return {
    type: 'PURCHASE_ORDER_SESSION',
    subtype: 'Merchant',
    meta: {
      prefix: 'SUM',
      hash: match?.groups?.hash ?? '',
    },
  }
}
export const parseUserBadge = (barcode = ''): UserLoginScanFormat => {
  const match = UserBadgeRegex.exec(barcode)
  return {
    type: 'USER_LOGIN',
    subtype: 'QuickLogin',
    meta: {
      userId: +(match?.groups?.userId ?? 0),
      checksum: match?.groups?.checksum ?? '',
    },
  }
}
export const parseServiceTicket = (barcode = ''): ServiceTicketScanFormat => {
  const serviceTicketId = barcode.replace(/\//g, '').split('-')?.[1] ?? 0
  return {
    type: 'SERVICE_TICKET',
    subtype: 'Merchant',
    meta: {
      serviceTicketId: +serviceTicketId,
    },
  }
}
export const parseScan = (barcode = ''): ScanResult => {
  const scanTypes: ScanType[] = [
    { type: 'USER_LOGIN', test: userBadgeScanTest, parser: parseUserBadge },
    {
      type: 'MERCHANT_CREDIT',
      test: merchantCreditScanTest,
      parser: parseMerchantCredit,
    },
    {
      type: 'PURCHASE_ORDER',
      test: purchaseOrderSessionScanTest,
      parser: parsePurchaseOrder,
    },
    {
      type: 'PURCHASE_ORDER_SESSION',
      test: purchaseOrderSessonGroupScanTest,
      parser: parsePurchaseOrderSession,
    },
    {
      type: 'SERVICE_TICKET',
      test: serviceTicketScanTest,
      parser: parseServiceTicket,
    },
    {
      type: 'INBOUND_RMA',
      test: inboundReturnScanTest,
      parser: parseInboundReturn,
    },
    {
      type: 'WAREHOUSE_BIN',
      test: chaosBarcodeScanTest,
      parser: parseChaosBarcodeScan,
    },
    {
      type: 'WAREHOUSE_DIVISION',
      test: chaosBarcodeScanTest,
      parser: parseChaosBarcodeScan,
    },
    {
      type: 'PRODUCT_GTIN',
      test: productScanTest,
      parser: parseProductBarcode,
    },
    {
      type: 'TRACKING_NUMBER',
      test: trackingNumberScanTest,
      parser: parseTrackingNumber,
    },
    // * Non-GTIN product catch-all (see isProductBarcode): LAST so tracking / gift / wrapped
    // * division keep precedence. Replaces the former FBA_SKU classification — every FNSKU pattern
    // * is claimed by one of the two PRODUCT_GTIN entries (the digit-run test above, then this).
    {
      type: 'PRODUCT_GTIN',
      test: productBarcodeScanTest,
      parser: parseProductBarcode,
    },
  ]
  for (const scanType of scanTypes) {
    if (scanType.test(barcode)) {
      return scanType.parser(barcode)
    }
  }
  return {
    type: 'UNKNOWN_FORMAT',
    subtype: 'Merchant',
  }
}
